import { Tooltip } from "@mui/material";

import { StatusChip } from "./StatusChip";

/**
 * How much room the badge has, which is the only thing that varies about it.
 *
 * `compact` abbreviates to a letter for grid cells, where the badge shares a
 * column with the value the reader came for and a word would push it out.
 * `full` spells the word where reading is the point. `long` names the thing in
 * full, for the one place the badge IS the label — the accumulation clock's
 * rule source, where the limit beside it now reads only "60 days".
 */
export type EpisodicBadgeDensity = "compact" | "full" | "long";

/**
 * The one marker for "this is accumulating under an episodic event", wherever
 * that fact shows up.
 *
 * A component rather than a color and a string, because it was previously
 * neither: the container grid drew a grey chip and the clock card an orange
 * one, for the same fact, and a reader had no way to know they meant the same
 * thing. Blue and unfilled is now the episodic marker everywhere.
 *
 * The abbreviation carries the full word as a tooltip and as its accessible
 * name, so "E" is never the only thing a reader (or a screen reader) gets.
 */
export function EpisodicBadge({ density = "full" }: { density?: EpisodicBadgeDensity }) {
  if (density !== "compact") {
    return (
      <StatusChip
        color="info"
        variant="outlined"
        label={density === "long" ? "Episodic event" : "Episodic"}
      />
    );
  }
  return (
    <Tooltip title="Accumulating under an episodic event">
      <span style={{ display: "inline-flex" }}>
        <StatusChip color="info" variant="outlined" label="E" aria-label="Episodic" />
      </span>
    </Tooltip>
  );
}
